import * as React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Cloud, Cpu, ShieldCheck } from "lucide-react";
import { CatalogHero, CatalogLayout, FeatureList } from "../components/catalog";
import { useContactModal } from "../context/contactModal";
import "../styles/training.css";

/*
 * Training & Compute (/training)
 * Structure adapted from PhysenAI's /en/training page, rewritten for Actrium's U.S. buyers.
 *
 * FACT RULES (see docs/TRAINING_FACTS.md):
 *  - Actrium is BUILDING its own U.S. GPU data center; first servers are ordered; it is NOT in service yet.
 *  - Actrium is a member of NVIDIA Inception (text only; the official badge comes from the Inception portal).
 *  - Do not add GPU models, quantities, location, dates, pricing, certifications or an API console URL
 *    until confirmed. Spots that need a confirmed fact are tagged `FACT NEEDED`.
 */

const SECTIONS = [
  ["infrastructure", "U.S. infrastructure"],
  ["why", "Why Actrium"],
  ["train", "Train & fine-tune"],
  ["compute", "GPU compute & model API"],
  ["inference", "Hosted inference"],
  ["engagement", "Engagement"],
  ["security", "Security"],
  ["pricing", "Pricing"],
  ["faq", "FAQ"],
];

export const DEPLOYMENT_MODES = [
  {
    label: "Hosted compute",
    title: "Host the model remotely. Keep control on site.",
    // FACT NEEDED: interim hosting location/provider before the Actrium facility is in service.
    text: "Run inference off the robot when connectivity is stable and data transfer is authorized. Where the model is hosted is confirmed in writing for each project; hosting in Actrium's own U.S. data center becomes available once the facility is in service.",
    benefit: "Share model environments and versions without duplicating large-memory compute on every robot.",
    condition:
      "Validate tail latency, jitter, concurrency, queueing and transfer cost. Retain necessary local compute and safe fallback.",
  },
  {
    label: "Private-site compute",
    title: "Keep the model close to the robot.",
    text: "Run models on an on-site server or private cluster for laboratories and projects with restricted data movement, using a controlled local network.",
    benefit: "Centralize compute and model maintenance while reducing dependence on public networks.",
    condition:
      "Test server failure, network disruption and contention. Define access isolation, logging and operational responsibilities.",
  },
  {
    label: "Onboard inference",
    title: "Run locally when the task requires it.",
    text: "For unreliable networks, offline operation or tighter response requirements, use models that fit the robot and have passed task-specific validation.",
    benefit:
      "Execution can operate without a public network connection, with tighter control over data movement.",
    condition:
      "Measure model capacity, inference time, power and cooling. Local deployment still requires system safety validation.",
  },
];

const INFRASTRUCTURE = [
  {
    status: "Under construction",
    title: "Our own U.S. GPU data center",
    // FACT NEEDED: location (state/metro), target capacity, expected in-service date.
    text: "Actrium AI is building its own GPU data center in the United States. Location, capacity and service dates will be published here once confirmed.",
  },
  {
    status: "Ordered",
    title: "First servers on order",
    // FACT NEEDED: GPU model(s), server/node count, interconnect and storage, delivery timing.
    text: "The first servers for the facility have been ordered. Hardware configurations will be listed here as they are confirmed.",
  },
  {
    status: "Member",
    title: "NVIDIA Inception",
    // Text-only mention. Official member badge: download from the NVIDIA Inception portal and follow its usage guide.
    text: "Actrium AI is a member of NVIDIA Inception, NVIDIA's program for startups.",
  },
];

const WHY = [
  [
    "Run models your robot can't host.",
    "When a policy or vision model needs more GPU memory than a robot can carry, we evaluate server-class inference so the heavy compute stays off the robot.",
  ],
  [
    "Share compute across robots.",
    "Compatible robots can share one model service instead of duplicating environments and idle GPUs. Whether that lowers cost depends on GPU, network, storage and operations together, and we model it with you first.",
  ],
  [
    "Ship skill versions, not device images.",
    "Models, adapter configurations and evaluation records are managed centrally, released to devices in stages and rolled back to a validated version if a regression appears.",
  ],
];

const TRAINING_STEPS = [
  [
    "1. Pilot run",
    "Train on a representative slice to measure throughput, memory use and early signal, then estimate full-run resources.",
  ],
  [
    "2. Full training",
    "Run on an agreed dataset version with fixed splits, seeds and environment dependencies.",
  ],
  [
    "3. Offline evaluation",
    "Replay held-out episodes and compare against baselines under the same data and training budget.",
  ],
  [
    "4. Physical evaluation",
    "Supervised trials on the target robot, with success rates tied to named tasks, objects, starting states and trial counts.",
  ],
];

const WHERE_IT_RUNS = [
  [
    "Actrium U.S. GPU data center",
    "Under construction; first servers ordered",
    "Training, fine-tuning and hosted inference once the facility is in service",
  ],
  [
    // FACT NEEDED: interim capacity before the facility opens (provider/partner, region); keep generic until confirmed.
    "Project-scoped compute",
    "Where it runs is confirmed in writing per project",
    "Pilots and training runs before our own facility comes online",
  ],
  [
    "Your cloud or GPU cluster",
    "We deliver configurations, containers and experiment records",
    "Teams with existing capacity, or data that must stay in their environment",
  ],
  [
    "On-site workstation",
    "RTX 4070 / 4080 / 4090 / 5070 / 5080 / 5090 options",
    "Collection, preprocessing and local inference; compatibility confirmed per configuration",
  ],
];

const GATEWAY_PATH = [
  [
    "Model service",
    "Task understanding, policy inference and candidate action chunks. Receives authorized observations and returns time-limited results.",
  ],
  [
    "On-site gateway",
    "Timestamps, state alignment, action mapping and validity checks. Only actions that pass constraint checks are executed.",
  ],
  [
    "Robot local control",
    "Servo control, limits, emergency stop and validated fault handling. Results and exceptions flow back to authorized logs for evaluation and recollection.",
  ],
];

const WHOLE_PATH = [
  [
    "Model",
    "Evaluate quantization, inference steps and action-chunk strategy against task performance and response time.",
  ],
  [
    "GPU kernels",
    "Evaluate operator, compiler and memory-access optimizations per model and hardware; re-validate numerics and policy behavior after acceleration.",
  ],
  [
    "Network",
    "Tune observation encoding, transport and connection handling to control bandwidth, jitter and stale data, not just average latency.",
  ],
  [
    "Placement",
    "Choose where inference runs by robot location and site conditions, with warm-up and concurrency scheduling or private-site compute.",
  ],
];

const ENGAGEMENT = [
  [
    "Package the skill",
    "Version weights, preprocessing, input and output contracts, robot adapters and operating conditions together.",
    "Skill manifest and adapter configuration",
  ],
  [
    "Deploy the inference service",
    "Configure resources, warm-up, monitoring and concurrency isolation on hosted or private GPUs.",
    "Deployment configuration and performance report",
  ],
  [
    "Integrate on the robot",
    "Align cameras, state, coordinate frames and action space through the gateway; test action expiry, bounds and timeouts.",
    "Interface adaptation and fault-test results",
  ],
  [
    "Evaluate and release",
    "Offline replay and shadow inference first, then supervised trials and staged rollout to compatible devices.",
    "Acceptance report and rollback procedure",
  ],
];

const DELIVERABLES = [
  [
    "Experiment configuration",
    "Dataset version, splits, preprocessing, hyperparameters, random seeds and environment dependencies.",
  ],
  ["Model artifacts", "Agreed weights, configurations, licenses and third-party dependency manifest."],
  [
    "Evaluation report",
    "Fixed tasks and trial conditions, success rates, interventions, duration and failure categories.",
  ],
  ["Resource records", "GPU type, runtime, storage and API consumption recorded separately."],
];

const FAQ = [
  [
    "Is Actrium's own data center available today?",
    "Not yet. We are building our own GPU data center in the United States, and the first servers have been ordered. Until it is in service, compute for each project is scoped individually, and we confirm in writing where your training and inference will run before any data moves.",
  ],
  [
    "What happens if the network is slow or drops?",
    "Heartbeats, observation freshness and action validity windows are checked continuously. On a timeout or state mismatch, stale actions are rejected and the robot enters a controlled stop, a safe hold or a validated fallback, with human takeover preserved. When the connection returns, state is re-synchronized and old commands are never replayed. High-frequency contact control never depends on an internet round trip.",
  ],
  [
    "How do you know a deployment actually works?",
    "We measure observation-to-action latency end to end, not just GPU inference time, and record P50, P95 and P99 latency, jitter, packet loss, queueing and cold-start time. We then check task success, interventions, timeout handling, rollback and total operating cost. We don't quote a fixed latency or a general success rate in advance.",
  ],
  [
    "How is this different from renting GPUs or calling a model API?",
    "A model API gives you access to models, and GPU compute gives you resources. A robot skill also needs its own model runtime, a device adapter and a local control path. We build and validate those per project and link execution logs to data versions and recollection.",
  ],
  [
    "Can we train in our own cloud or cluster?",
    // FACT NEEDED: confirm bring-your-own-cloud/cluster support and any constraints.
    "Where it fits the project, we deliver configurations, containers and experiment records for your environment. Compatibility with your cloud account or cluster is confirmed during scoping.",
  ],
  [
    "Where is our data stored?",
    "Customer data is stored and processed in the United States by default. Ownership, usage scope, retention, access and any third-party processing are agreed in writing before transfer.",
  ],
];

export function InferenceDiagram({ mode }) {
  return (
    <figure className="service-inference" aria-label="Model inference and local robot control workflow">
      <div className="service-inference-node">
        <Cloud aria-hidden="true" />
        <strong>{["Hosted inference", "Private-site inference", "Onboard inference"][mode]}</strong>
        <span>Model + adapter + version</span>
      </div>
      <div className="service-inference-link">
        <span>Observations ↑</span>
        <ArrowRight aria-hidden="true" />
        <span>Validated results ↓</span>
      </div>
      <div className="service-inference-node">
        <Cpu aria-hidden="true" />
        <strong>Robot gateway</strong>
        <span>State alignment · Action expiry</span>
      </div>
      <figcaption>
        <ShieldCheck aria-hidden="true" /> Local control, emergency stop and takeover remain on the robot.
      </figcaption>
    </figure>
  );
}

export function ComputeServices() {
  return (
    <section className="catalog-wrap catalog-section" id="compute">
      <p className="catalog-kicker">GPU compute & model API</p>
      <h2>Two services for two kinds of work.</h2>
      <div className="catalog-two service-compute-lanes">
        <article>
          <h3>GPU compute</h3>
          <p>
            Environments for data processing, VLA and world-model training. We match GPU memory, software
            dependencies and storage to the workload and record usage by resource type.
          </p>
          <div className="service-compute-flow">
            <span>Experiment</span>
            <ArrowRight aria-hidden="true" />
            <strong>GPU</strong>
            <ArrowRight aria-hidden="true" />
            <span>Artifacts</span>
          </div>
        </article>
        <article>
          <h3>Model API access</h3>
          {/* FACT NEEDED: is an Actrium-branded, U.S.-hosted model API/console available? If yes, add its name and link. */}
          <p>
            When a project uses hosted vision or language models for annotation, retrieval or quality review,
            we agree which models are used, who can access them and usage limits with you up front.
          </p>
          <div className="service-compute-flow">
            <span>Data tools</span>
            <ArrowRight aria-hidden="true" />
            <strong>API</strong>
            <ArrowRight aria-hidden="true" />
            <span>Models</span>
          </div>
        </article>
      </div>
      <p className="catalog-note">
        API access and GPU resources are scoped separately. Neither is a robot control interface. Availability
        and authorized data transfer are confirmed per project.
      </p>
      <div className="catalog-table-scroll training-runs" tabIndex={0} aria-label="Where training and inference run">
        <table>
          <caption>Where training and inference run</caption>
          <thead>
            <tr>
              <th scope="col">Option</th>
              <th scope="col">Status</th>
              <th scope="col">Best for</th>
            </tr>
          </thead>
          <tbody>
            {WHERE_IT_RUNS.map(([option, status, fit]) => (
              <tr key={option}>
                <th scope="row">{option}</th>
                <td data-label="Status">{status}</td>
                <td data-label="Best for">{fit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SectionNav() {
  return (
    <nav className="catalog-wrap catalog-tabs training-nav" aria-label="On this page">
      {SECTIONS.map(([id, label]) => (
        <a href={`#${id}`} key={id}>
          {label}
        </a>
      ))}
    </nav>
  );
}

function DeploymentModes({ mode, setMode }) {
  const current = DEPLOYMENT_MODES[mode];
  return (
    <>
      <div className="catalog-filters" role="tablist" aria-label="Inference deployment">
        {DEPLOYMENT_MODES.map((item, i) => (
          <button
            role="tab"
            id={`deployment-tab-${i}`}
            aria-selected={mode === i}
            aria-pressed={mode === i}
            aria-controls="deployment-panel"
            tabIndex={mode === i ? 0 : -1}
            onClick={() => setMode(i)}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
                event.preventDefault();
                const next = (mode + (event.key === "ArrowRight" ? 1 : 2)) % 3;
                setMode(next);
                document.getElementById(`deployment-tab-${next}`)?.focus();
              }
            }}
            key={item.label}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div
        className="catalog-mode-panel catalog-two"
        role="tabpanel"
        id="deployment-panel"
        aria-labelledby={`deployment-tab-${mode}`}
      >
        <div>
          <h3>{current.title}</h3>
          <p>{current.text}</p>
        </div>
        <div>
          <p>{current.benefit}</p>
          <p className="catalog-note">{current.condition}</p>
        </div>
      </div>
    </>
  );
}

export function TrainingPage() {
  const [mode, setMode] = React.useState(0);
  const { openModal, openInquiry } = useContactModal();
  return (
    <CatalogLayout title="Training & Compute">
      <div className="training-page">
        <div className="catalog-wrap pt-28">
          <Link className="catalog-text-link" to="/solutions">
            Solutions /
          </Link>
        </div>
        <CatalogHero
          label="Train · Deploy · Operate"
          title="Training & Compute"
          intro="From robot data to deployed skills. Actrium connects your datasets, model training, hosted inference and on-robot execution in one traceable workflow, and we are building our own GPU data center in the United States to run it."
        />
        <div className="catalog-wrap training-actions">
          <button
            className="catalog-button"
            onClick={() => openInquiry("Training & Compute", "Scope a Training Pilot")}
          >
            Scope a training pilot
          </button>
          <a className="catalog-text-link" href="#inference">
            Compare deployment options ↓
          </a>
        </div>
        <SectionNav />

        <section className="catalog-band" id="infrastructure">
          <div className="catalog-wrap catalog-section">
            <p className="catalog-kicker">U.S. infrastructure</p>
            <h2>Building our own U.S. GPU data center.</h2>
            <p className="catalog-section-intro">
              Actrium AI is building its own GPU data center in the United States, and the first servers have
              been ordered. The facility is not yet in service. Until it comes online, compute for each
              project is scoped individually, and we confirm in writing where your training and inference will
              run before any data moves.
            </p>
            <div className="catalog-features">
              {INFRASTRUCTURE.map((item) => (
                <article key={item.title}>
                  <span className="training-pill">{item.status}</span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
            <p className="catalog-note">
              NVIDIA and NVIDIA Inception are trademarks and/or registered trademarks of NVIDIA Corporation in
              the U.S. and other countries.
            </p>
          </div>
        </section>

        <section className="catalog-wrap catalog-section" id="why">
          <p className="catalog-kicker">Why Actrium</p>
          <h2>Compute organized around robot learning.</h2>
          <FeatureList items={WHY} />
        </section>

        <section className="catalog-band" id="train">
          <div className="catalog-wrap catalog-section">
            <p className="catalog-kicker">Train & fine-tune</p>
            <h2>Training runs that start from your data.</h2>
            <div className="catalog-two">
              <div>
                <p>
                  Bring datasets from Actrium data collection and the Data Platform, or from your own storage.
                  We size GPU memory and count from model size, sequence length, visual inputs, batch size and
                  precision, then confirm the estimate with a short pilot run before committing to a full
                  training job.
                </p>
                <Link className="catalog-text-link" to="/solutions/capture/data-platform">
                  Data Platform →
                </Link>
              </div>
              <div>
                <h3>Typical workloads</h3>
                <ul className="catalog-checklist">
                  <li>Embodied data processing and dataset export</li>
                  <li>VLA fine-tuning on task-specific demonstrations</li>
                  <li>World-model experiments</li>
                  <li>Imitation-learning baselines for Atlas robot learning workstations</li>
                  <li>LeRobot-compatible data and training workflows, tested per version</li>
                </ul>
              </div>
            </div>
            <div className="catalog-features catalog-four">
              {TRAINING_STEPS.map(([title, text]) => (
                <article key={title}>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
            <p className="catalog-note">
              Model support, data-upload rights and training services are confirmed per project. We don't
              promise fixed training times or general success rates.
            </p>
          </div>
        </section>

        <ComputeServices />

        <section className="catalog-wrap catalog-section catalog-first" id="compute-config">
          <h2>Configure three distinct workloads.</h2>
          <FeatureList
            items={[
              [
                "Collection and processing",
                "Local workstations support sensors, replay and preprocessing. Prioritize interface bandwidth, storage throughput, timing and sustained operation.",
              ],
              [
                "Training and fine-tuning",
                "Select GPU memory and count for model size, sequence length, visual inputs, batch size and precision. Estimate resources after a pilot run.",
              ],
              [
                "Online robot inference",
                "Choose hosted, private-site or onboard deployment for latency, network, power and data permissions. Adapt the model interface and runtime to the robot.",
              ],
            ]}
          />
          <div className="catalog-callout">
            <h3>Workstation configuration options</h3>
            <p>
              RTX 4070 / 4080 / 4090 / 5070 / 5080 / 5090 options require confirmation of memory, power,
              cooling, chassis and software compatibility. High-power configurations are not assumed to fit
              onboard a mobile robot. Hosted compute and model API usage are scoped separately.
            </p>
          </div>
        </section>

        <section className="catalog-wrap catalog-section" id="inference">
          <p className="catalog-kicker">Hosted inference for robots</p>
          <h2>Cloud intelligence. Local control.</h2>
          <p className="catalog-section-intro">
            Deploy a trained policy as a monitored, versioned skill service. An on-site gateway connects the
            robot, and emergency stop and low-level control never wait on the network.
          </p>
          <InferenceDiagram mode={mode} />
          <h3 className="training-subhead">Choose where inference runs.</h3>
          <DeploymentModes mode={mode} setMode={setMode} />
          <div className="catalog-features">
            {GATEWAY_PATH.map(([title, text]) => (
              <article key={title}>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
          <div className="catalog-callout">
            <h3>High-frequency control stays with the robot.</h3>
            <p>
              Align camera inputs, state, coordinates and action spaces through a local gateway. Validate
              action expiry, bounds, timeouts, emergency stop and human takeover before enabling execution.
            </p>
          </div>
        </section>

        <section className="catalog-band">
          <div className="catalog-wrap catalog-section">
            <h2>Optimize the whole path, not just the GPU.</h2>
            <div className="catalog-features catalog-four">
              {WHOLE_PATH.map(([title, text]) => (
                <article key={title}>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
            <p className="catalog-note">
              We compare hosted, private-site and onboard options on the same task baseline and don't assume
              hosted is better.
            </p>
          </div>
        </section>

        <section className="catalog-wrap catalog-section" id="engagement">
          <p className="catalog-kicker">How an engagement runs</p>
          <h2>A versioned path from model to robot.</h2>
          <div className="catalog-features catalog-four">
            {ENGAGEMENT.map(([title, text, delivers], i) => (
              <article key={title}>
                <h3>
                  {i + 1}. {title}
                </h3>
                <p>{text}</p>
                <p className="catalog-note">
                  <strong>Delivers:</strong> {delivers}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section className="catalog-wrap catalog-section catalog-first">
          <h2>Deliver a reproducible experiment.</h2>
          <div className="catalog-features catalog-four">
            {DELIVERABLES.map(([title, text]) => (
              <article key={title}>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
          <p className="catalog-note">
            Model availability, API permissions, data upload and training services require project
            confirmation. An unvalidated model is not described as ready to run on an arbitrary robot;
            training duration and success rates depend on the task.
          </p>
        </section>

        <section className="catalog-dark">
          <div className="catalog-wrap catalog-section catalog-two">
            <article>
              <h2>Understanding and data processing</h2>
              <p>
                Vision and language APIs can support task descriptions, candidate annotations, retrieval and
                quality review. Their outputs need review and are not automatically action ground truth.
              </p>
            </article>
            <article>
              <h2>Robot policy inference</h2>
              <p>
                Specify camera inputs, state fields, action spaces, end effectors and control frequency.
                Correct offline output does not establish safe physical execution.
              </p>
            </article>
          </div>
        </section>

        <section className="catalog-band" id="security">
          <div className="catalog-wrap catalog-section">
            <p className="catalog-kicker">Security & data residency</p>
            <h2>Your data, handled under a written data contract.</h2>
            <div className="catalog-two">
              <div>
                <p>
                  Customer data is stored and processed in the United States by default. Before any transfer,
                  we agree in writing on ownership, usage scope, retention, access and any third-party
                  processing.
                </p>
                <div className="catalog-callout">
                  <h3>Compliance requirements</h3>
                  {/* FACT NEEDED: SOC 2 / ISO 27001 status, encryption, SSO/RBAC, audit logging, DPA/MSA availability. */}
                  <p>
                    Tell us your security, compliance and procurement requirements at the start. We confirm in
                    writing what applies to your project before work begins.
                  </p>
                </div>
              </div>
              <ul className="catalog-checklist">
                <li>U.S. storage and processing of customer data by default.</li>
                <li>Project data leaves your environment only with written authorization.</li>
                <li>
                  Access isolation, logging and operational responsibilities defined for each deployment.
                </li>
                <li>Evaluation data separated by participant, scene or date.</li>
                <li>
                  API keys stay server-side; sensitive raw data is not sent to third-party models without
                  specific authorization.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section className="catalog-wrap catalog-section" id="pricing">
          <p className="catalog-kicker">Pricing & engagement</p>
          <h2>Scoped to the task, itemized on the invoice.</h2>
          <div className="catalog-two">
            <div>
              {/* FACT NEEDED: pricing model (GPU-hour, reserved, pilot package, academic) and any public "from" price. */}
              <p>
                Most projects start with a pilot on one task. After the pilot, GPU compute, model API usage
                and engineering services are quoted as separate line items, so you can see what each part of
                the work costs.
              </p>
              <button
                className="catalog-button"
                onClick={() => openInquiry("Training & Compute", "Request a Quote")}
              >
                Request a quote
              </button>
            </div>
            <div>
              <h3>What we need to quote</h3>
              <ul className="catalog-checklist">
                <li>Target robot, cameras and action space</li>
                <li>Dataset size, modalities and current format</li>
                <li>Model family and training objective</li>
                <li>Where inference must run, and your latency requirements</li>
                <li>Data residency, security and procurement requirements</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="catalog-wrap catalog-section catalog-first training-faq" id="faq">
          <p className="catalog-kicker">FAQ</p>
          <h2>Questions teams ask before training with us.</h2>
          {FAQ.map(([question, answer], i) => (
            <details key={question} open={i === 0}>
              <summary>{question}</summary>
              <p>{answer}</p>
            </details>
          ))}
        </section>

        <section className="catalog-cta">
          <div className="catalog-wrap">
            <div>
              <p className="catalog-kicker">Build with Actrium</p>
              <h2>Have a model or a robot?</h2>
              <p>Start with a deployment assessment for one task.</p>
            </div>
            <button className="catalog-button" onClick={openModal}>
              Discuss your project
            </button>
          </div>
        </section>
      </div>
    </CatalogLayout>
  );
}
